import { Fragment, useState } from "react";

import {
  ChevronDown,
  ChevronRight,
  Edit,
  MoreHorizontal,
  Star,
  Trash2,
} from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import type { RecipeIngredient, SkuRecipe } from "~/types/production";

interface RecipesTableProps {
  recipes: SkuRecipe[];
  isLoading: boolean;
  onEdit: (recipe: SkuRecipe) => void;
  onDelete: (recipe: SkuRecipe) => void;
  onSetDefault: (recipe: SkuRecipe) => void;
}

function getIngredientName(ingredient: RecipeIngredient): string {
  return (
    ingredient.raw_material?.name || ingredient.oil_type?.name || "Sin nombre"
  );
}

function getIngredientUnit(ingredient: RecipeIngredient): string {
  if (ingredient.oil_type) return "L";
  return ingredient.raw_material?.unit || "u";
}

export function RecipesTable({
  recipes,
  isLoading,
  onEdit,
  onDelete,
  onSetDefault,
}: RecipesTableProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (recipes.length === 0) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        No se encontraron recetas
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[40px]"></TableHead>
          <TableHead>Código</TableHead>
          <TableHead>Producto</TableHead>
          <TableHead>Nombre</TableHead>
          <TableHead>Ingredientes</TableHead>
          <TableHead>Estado</TableHead>
          <TableHead className="w-[70px]"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {recipes.map((recipe, index) => {
          const isExpanded = expandedIds.has(recipe.id);
          const ingredients = recipe.ingredients ?? [];
          return (
            <Fragment key={recipe.id}>
              <TableRow
                className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                onClick={() => toggleExpanded(recipe.id)}
              >
                <TableCell className="pr-0">
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </TableCell>
                <TableCell className="font-medium">
                  {recipe.product?.sku || "—"}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {recipe.product?.short_name || "—"}
                </TableCell>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {recipe.name}
                    {recipe.is_default && (
                      <Star className="h-4 w-4 fill-yellow-500 text-yellow-500" />
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="secondary">
                    {ingredients.length} ingredientes
                  </Badge>
                </TableCell>
                <TableCell>
                  {recipe.is_default ? (
                    <Badge variant="default">Por defecto</Badge>
                  ) : (
                    <Badge variant="outline">Alternativa</Badge>
                  )}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onEdit(recipe)}>
                        <Edit className="mr-2 h-4 w-4" />
                        Editar
                      </DropdownMenuItem>
                      {!recipe.is_default && (
                        <DropdownMenuItem onClick={() => onSetDefault(recipe)}>
                          <Star className="mr-2 h-4 w-4" />
                          Establecer por defecto
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => onDelete(recipe)}
                        className="text-red-600"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Eliminar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
              {isExpanded && (
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  <TableCell></TableCell>
                  <TableCell colSpan={6} className="py-3">
                    {ingredients.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        Esta receta no tiene ingredientes
                      </p>
                    ) : (
                      <div className="space-y-1.5">
                        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          Ingredientes
                        </p>
                        <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
                          {ingredients.map((ing) => (
                            <div
                              key={ing.id}
                              className="flex items-center justify-between rounded-md border bg-background px-3 py-2 text-sm"
                            >
                              <span className="font-medium">
                                {getIngredientName(ing)}
                              </span>
                              <span className="text-muted-foreground">
                                {ing.quantity_per_box} {getIngredientUnit(ing)}{" "}
                                / caja
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}
